/**
 * Validation Utilities
 * Reusable validation functions for forms and inputs
 */

/**
 * Validates if a value is not empty
 * @param {any} value - Value to validate
 * @returns {Object} Validation result with valid boolean and error message
 */
export const required = (value) => ({
  valid: value !== null && value !== undefined && value.toString().trim() !== '',
  error: 'This field is required'
});

/**
 * Validates email format
 * @param {string} value - Email to validate
 * @returns {Object} Validation result
 */
export const email = (value) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return {
    valid: !value || emailRegex.test(value),
    error: 'Please enter a valid email address'
  };
};

/**
 * Validates phone number (10-15 digits)
 * @param {string} value - Phone number to validate
 * @returns {Object} Validation result
 */
export const phone = (value) => {
  const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;
  return {
    valid: !value || phoneRegex.test(value.replace(/\s/g, '')),
    error: 'Please enter a valid phone number'
  };
};

/**
 * Validates minimum length
 * @param {number} minLength - Minimum required length
 * @returns {Function} Validator function
 */
export const minLength = (minLength) => (value) => ({
  valid: !value || value.toString().length >= minLength,
  error: `Minimum ${minLength} characters required`
});

/**
 * Validates maximum length
 * @param {number} maxLength - Maximum allowed length
 * @returns {Function} Validator function
 */
export const maxLength = (maxLength) => (value) => ({
  valid: !value || value.toString().length <= maxLength,
  error: `Maximum ${maxLength} characters allowed`
});

/**
 * Validates numeric value
 * @param {any} value - Value to validate
 * @returns {Object} Validation result
 */
export const numeric = (value) => ({
  valid: !value || !isNaN(Number(value)),
  error: 'Please enter a valid number'
});

/**
 * Validates positive number
 * @param {any} value - Value to validate
 * @returns {Object} Validation result
 */
export const positive = (value) => ({
  valid: !value || (Number(value) > 0),
  error: 'Please enter a positive number'
});

/**
 * Validates minimum value
 * @param {number} min - Minimum value
 * @returns {Function} Validator function
 */
export const minValue = (min) => (value) => ({
  valid: !value || Number(value) >= min,
  error: `Value must be at least ${min}`
});

/**
 * Validates maximum value
 * @param {number} max - Maximum value
 * @returns {Function} Validator function
 */
export const maxValue = (max) => (value) => ({
  valid: !value || Number(value) <= max,
  error: `Value must be at most ${max}`
});

/**
 * Validates URL format
 * @param {string} value - URL to validate
 * @returns {Object} Validation result
 */
export const url = (value) => {
  try {
    if (!value) return { valid: true, error: '' };
    new URL(value);
    return { valid: true, error: '' };
  } catch {
    return { valid: false, error: 'Please enter a valid URL' };
  }
};

/**
 * Validates date format (YYYY-MM-DD)
 * @param {string} value - Date to validate
 * @returns {Object} Validation result
 */
export const date = (value) => {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  const isValidFormat = dateRegex.test(value);
  const isValidDate = !isNaN(Date.parse(value));
  return {
    valid: !value || (isValidFormat && isValidDate),
    error: 'Please enter a valid date (YYYY-MM-DD)'
  };
};

/**
 * Validates that value matches another value (e.g., password confirmation)
 * @param {any} compareValue - Value to compare against
 * @returns {Function} Validator function
 */
export const matches = (compareValue) => (value) => ({
  valid: value === compareValue,
  error: 'Values do not match'
});

/**
 * Combines multiple validators
 * @param {Function[]} validators - Array of validator functions
 * @returns {Function} Combined validator function
 */
export const combine = (...validators) => (value) => {
  for (const validator of validators) {
    const result = validator(value);
    if (!result.valid) {
      return result;
    }
  }
  return { valid: true, error: '' };
};

/**
 * Validates an entire form object
 * @param {Object} formData - Form data object
 * @param {Object} validationRules - Validation rules object
 * @returns {Object} Validation errors object
 */
export const validateForm = (formData, validationRules) => {
  const errors = {};

  Object.keys(validationRules).forEach(field => {
    const validator = validationRules[field];
    const result = validator(formData[field]);

    if (!result.valid) {
      errors[field] = result.error;
    }
  });

  return errors;
};

/**
 * Checks if form has any errors
 * @param {Object} errors - Errors object
 * @returns {boolean} True if form has errors
 */
export const hasErrors = (errors) => {
  return Object.keys(errors).length > 0;
};

/**
 * Checks if a cart item is a service/labour type for VAT calculation purposes.
 * Matches by type field or by description keywords.
 * @param {Object} item - Cart item with type and/or description fields
 * @returns {boolean} True if item is a service or labour item
 */
export const isServiceOrLabour = (item) => {
  if (!item) return false;
  const type = (item.type || '').toLowerCase();
  if (type === 'service') return true;
  const desc = (item.description || item.partName || '').toLowerCase();
  return desc.includes('labour') || desc.includes('labor');
};

export default {
  required,
  email,
  phone,
  minLength,
  maxLength,
  numeric,
  positive,
  minValue,
  maxValue,
  url,
  date,
  matches,
  combine,
  validateForm,
  hasErrors,
  isServiceOrLabour,
};
